<!-- =============================================================================
  Dateless · The PlateX Reference — Phase 1 static structure.
  Copy verbatim from the approved v13 prototype; reserved blocks mark the
  sections owned by later phases (unindexed draft route only).
============================================================================== -->

<!-- chapter navigation (§6.1) -->
<nav class="chapnav" aria-label="Chapters"><div class="wrap">
  <a *ngFor="let c of chapters; trackBy: trackByChapter"
     [class.hot]="c.hot"
     href="#{{c.id}}"
     (click)="jumpTo(c.id, $event)">{{c.label}}</a>
</div></nav>

<!-- HERO (§6.2) -->
<section class="hero">
  <div class="wrap">
    <div class="hero-copy">
      <span class="eyebrow">The PlateX Reference</span>
      <h1 class="hero-title">Dateless<br>Registrations</h1>
      <p class="hero-lede">The first registrations ever issued in Britain.</p>
      <p>Created in 1903 and last issued in 1963, they remain the most sought-after registrations in the UK market. This is the PlateX reference to how the system worked and where every surviving format sits today.</p>
      <div class="hero-cta">
        <a class="btn green" href="#market" (click)="jumpTo('market', $event)">Begin the guide</a>
        <a class="btn ghost-d" href="#register" (click)="jumpTo('register', $event)">Explore the reference</a>
      </div>
    </div>
    <figure class="hero-figure">
      <!-- First-party render from the PlateX plate viewer (§6.2 "asset, not
           fabricated"): the viewer's own Ferrari 250 GT SWB wearing 25 O. The
           reserved frame below stays until the render resolves. -->
      <img *ngIf="heroImage" class="hero-img" [src]="heroImage"
           [alt]="'A ' + heroCarName + ' wearing the dateless registration ' + heroRegistration" />
      <div *ngIf="!heroImage" class="hero-reserve" role="img" aria-label="Hero image loading">
        <span class="plate-bs"><span class="ch">{{heroRegistration}}</span></span>
      </div>
      <figcaption class="figcap">{{heroCarName}}, wearing {{heroRegistration}}, a Birmingham dateless mark first issued more than a century ago.</figcaption>
    </figure>
  </div>
  <div class="hero-bench"><div class="wrap">
    <span class="lbl">The reference</span>
    <span class="story"><b>1903</b> first issued&nbsp;&middot;&nbsp;<b>1963</b> last issued&nbsp;&middot;&nbsp;a fixed set of registrations that will never grow again.</span>
  </div></div>
</section>

<!-- I · THE DATELESS MARKET (§6.3 — data lands at Phase 3 / Gate 2) -->
<section class="section tint" id="market"><div class="wrap">
  <div class="sec-head"><span class="eyebrow"><span class="ix">I</span>The dateless market</span>
    <h2>The market record.</h2>
    <p>Everything financial, in one place: the evidence, the record over time, where value sits across the formats, and the sales that set the benchmarks. Per-format figures are current to {{medianAsOf || 'the latest recorded sale'}}; the index runs to October 2026. Each is labelled by source.</p>
  </div>
  <!-- evidence tiles: only figures defensible from the underlying data (D4) -->
  <div class="evidence">
    <div class="ev"><div class="v">1903</div><div class="k">Earliest registration</div><div class="src">Reference</div></div>
    <div class="ev"><div class="v">{{medians ? (medians.source.corpusRows | number) : '131,337'}}</div><div class="k">Verified sales analysed</div><div class="src real">PlateX data</div></div>
    <div class="ev"><div class="v">≈ 5,500</div><div class="k">Sales a year (2023 to 2025)</div><div class="src real">PlateX data</div></div>
  </div>

  <h3 class="subhead">Ten years of prices: dateless against the whole market</h3>
  <div class="idx-grid">
    <div class="card">
      <span class="ck">Index · 2015 to 2026 · 2015 = 100</span>
      <div class="legendrow">
        <span class="lgi"><span class="sw sw-date"></span>Dateless</span>
        <span class="lgi"><span class="sw sw-mkt"></span>All plates (composite)</span>
      </div>
      <pla-dateless-index-chart [file]="indexFile"></pla-dateless-index-chart>
    </div>
    <div class="stat-col" *ngIf="statLastFull && statSinceBase">
      <div class="stat">
        <span class="ck">Dateless · {{statLastFull.year}} (last full year)</span>
        <div class="sv">{{statLastFull.value | number:'1.1-1'}}<span class="yoy">{{statLastFull.yoyPct > 0 ? '▲' : statLastFull.yoyPct < 0 ? '▼' : '–'}} {{statLastFull.yoyPct > 0 ? statLastFull.yoyPct : -statLastFull.yoyPct}}%</span></div>
        <div class="sc"><ng-container *ngIf="statLastFull.yoyPct !== 0; else levelYoy">{{statLastFull.yoyPct > 0 ? 'Up' : 'Down'}} {{statLastFull.yoyPct > 0 ? statLastFull.yoyPct : -statLastFull.yoyPct}}% on {{statLastFull.year - 1}}</ng-container><ng-template #levelYoy>Level with {{statLastFull.year - 1}}</ng-template>, and <b>{{statLastFull.multiple}}× its 2015 level</b>.</div>
      </div>
      <div class="stat">
        <span class="ck">Vs the whole market · since 2015</span>
        <div class="sv">+{{statSinceBase.dateless}}% <span class="vs">vs +{{statSinceBase.composite}}%</span></div>
        <div class="sc">Dateless has <b>{{statSinceBase.dateless >= statSinceBase.composite ? 'outpaced' : 'trailed'}} the all-plate index</b> since 2015.</div>
      </div>
    </div>
  </div>

  <div class="mkt-2col">
    <div class="vbf" *ngIf="vbfRows.length">
      <h3 class="subhead flush">Value by format</h3>
      <p class="fsub">The shortest shapes carry a steep premium. A three-character single is worth roughly <b>9 to 14 times</b> a five-character plate. Finite supply, and thin liquidity at the very top, do the rest.</p>
      <div class="frow" *ngFor="let row of vbfRows; trackBy: trackByToken" [class.ind]="row.rec.confidence === 'Indicative'">
        <span class="fc">{{row.rec.classification.display}}</span>
        <div class="bar-wrap"><div class="bar" [style.width.%]="row.barPct"></div></div>
        <span class="med">{{money(row.rec)}}<span class="i" *ngIf="row.rec.confidence === 'Indicative'">{{basisLabel(row.rec)}}</span></span>
      </div>
    </div>
    <div>
      <h3 class="subhead flush">Notable sales</h3>
      <p class="fsub">Real dateless results: the benchmarks the market remembers. Shown in the period black-and-silver.</p>
      <div class="sale" *ngFor="let s of notableSales; trackBy: trackBySale">
        <span class="plate-bs"><span class="ch">{{s.plate}}</span></span>
        <div class="sm"><b>{{s.when}}</b><br>{{s.note}}<br><span class="basis" [class.rep]="s.basis === 'reported'">{{s.basisLabel}}</span></div>
        <span class="sp2">{{s.price}}</span>
      </div>
    </div>
  </div>

  <p class="src-note"><b>Sources and caveats.</b> Index: PlateX Plate Price Index v2 (mix-adjusted, every plate sold at auction, like for like, all-in buyer cost: hammer plus premium plus VAT plus the £80 assignment fee), 2015 = 100, sold data to 21 October 2026; the 2026 point is year to date. Per-format medians: achieved sales; the per-format figures are read live from the PlateX sold-plate record and run to {{medianAsOf}}; per-format figures are on a rolling 24 month window; 1×1 rests on a single sale (1 F, RM Sotheby's, November 2025) and is shown as that sale rather than a median; 2×1 and 1×2 carry fewer than 100 window sales and are marked accordingly; the corpus is auction data and holds no private treaty sales, so the very top of a thin format can still be under-represented, and X 1 and G 1 remain absent; part of any 2025 to 2026 softening reflects an October 2025 auction-fee change, not market movement. Notable sales are stated on the PlateX verified-corpus basis where the sale is in the corpus; the 25 O corpus figure of £513,680 differs from the widely reported £518,480 because the bases differ. The 1 F result (£926,000, RM Sotheby's, November 2025) is the only 1×1 in the window and is carried from the same source as every other figure here. No UK registration has yet publicly sold for £1 million. A dated snapshot, not a live feed.</p>
</div></section>

<!-- II · WHY COLLECTORS CHOOSE DATELESS (§6.4) -->
<section class="section" id="appeal"><div class="wrap">
  <div class="sec-head"><span class="eyebrow"><span class="ix">II</span>Why collectors choose them</span>
    <h2>Made to belong on any car.</h2>
    <p>Set history and value aside for a moment. Ask a collector why they actually want a dateless mark, and the answer is rarely either.</p>
  </div>
  <div class="guide-grid">
    <div class="chap"><div class="cn">I</div><h3>No visible age</h3><p>A dateless mark contains no letter or figure that encodes a year, so it reveals nothing about the age of the car beneath it. It is the only class of UK registration of which this is true.</p></div>
    <div class="chap"><div class="cn">II</div><h3>Fits any vehicle</h3><p>With no age identifier, a dateless mark can be assigned to a vehicle of any age, so an owner can carry it from one car to the next as a collection changes. Registrations issued after 1963 encode a year and cannot legally be fitted to an older car.</p></div>
    <div class="chap"><div class="cn">III</div><h3>Built to last generations</h3><p>Because nothing about the mark dates, it can be retained and reassigned indefinitely, moving between vehicles and owners without ever looking out of period. Many have stayed with one family for decades.</p></div>
  </div>
</div></section>

<!-- III · HISTORIC ISSUING AUTHORITIES (§6.5 — archive lands at Phase 4 / Gate 2) -->
<section class="section tint" id="authorities"><div class="wrap">
  <div class="sec-head"><span class="eyebrow"><span class="ix">III</span>Historic Issuing Authorities &amp; the dateless story</span>
    <h2>The letters remember where they came from.</h2>
    <p>Every dateless mark began at a local authority, and its letters record which one. Select a letter to see its authority, an example registration, and a period photograph of a car that carried it. Some of Britain’s best-known registration stories turn out to be myths.</p>
  </div>
  <pla-dateless-authorities></pla-dateless-authorities>
</div></section>

<!-- III(b) · HISTORICAL TIMELINE (§6.6) -->
<section class="section" id="timeline"><div class="wrap">
  <div class="sec-head"><span class="eyebrow"><span class="ix">III</span>The story in sequence</span>
    <h2>One system, the whole of British motoring.</h2>
    <p>Dateless is the only registration system that spans the entire history of the car in Britain, from the first marks of 1903 to the collector market of today. Each stop is a real registration, and the format itself evolves as the country fills up.</p>
  </div>
  <div class="tl">
    <div class="mil" *ngFor="let m of timeline; trackBy: trackByMilestone">
      <div class="yr">{{m.yr}}<small *ngIf="m.sub">{{m.sub}}</small></div>
      <span class="dot" aria-hidden="true"></span>
      <div class="body">
        <span class="plate-bs" *ngIf="m.plate"><span class="ch">{{m.plate}}</span></span>
        <h4>{{m.heading}}</h4>
        <p [innerHTML]="m.html"></p>
      </div>
    </div>
  </div>
  <p class="src-note">{{timelineSources}}</p>
</div></section>

<!-- IV · UNDERSTANDING VALUE (§6.7 — prose now; the medians table lands at Phase 3) -->
<section class="section" id="value"><div class="wrap">
  <div class="sec-head"><span class="eyebrow"><span class="ix">IV</span>Understanding value</span>
    <h2>Why dateless sits at the top of the market.</h2>
    <p>A collector’s briefing, not a sales pitch. Dateless registrations occupy the top tier of the UK market because they combine three characteristics no later system can recreate.</p>
  </div>

  <div class="inv-grid">
    <div class="inv-card"><span class="inv-n">01</span><h3>Closed supply</h3><p>Issue ended in 1963, and no dateless mark can be created again. The set only contracts, as registrations are retained on vehicles, lost, or held off the market for years at a time. Supply cannot answer demand, which is rare among collectable assets.</p></div>
    <div class="inv-card"><span class="inv-n">02</span><h3>Universal compatibility</h3><p>Because a dateless mark fits a vehicle of any age, its buyer is the whole market rather than the owners of one model year, and that unrestricted demand is reflected in the price. Suffix, prefix and current registrations encode a year and cannot move to an older car.</p></div>
    <div class="inv-card"><span class="inv-n">03</span><h3>Proven demand</h3><p>These marks have been sought for more than a century, and the PlateX Index shows dateless values rising across the past decade. Demand has held through changes of fashion and economic cycle, which is what separates a collectable from a passing trend.</p></div>
  </div>

  <h3 class="subhead">Where value lies, and where a collection begins</h3>
  <p class="inv-lead">A collection rarely begins with a six-figure mark. Most start with the longer three-letter formats, which carry the same structural scarcity as the rarest plates but trade for a few thousand pounds. Value then rises sharply as characters fall away. The figures below are real achieved prices from the PlateX classification database of <b>{{medians ? (medians.source.corpusRows | number) : '131,337'}} verified dateless sales</b>, on a rolling <b>24 month</b> window, so the figures track what the market is paying now rather than an eight year average. Medians where the window carries enough sales to support one; where it does not, the observed range, or the single sale itself.</p>

  <div class="fmt-table" *ngIf="tableRows.length">
    <div class="ft-row ft-head"><span class="ft-f">Format</span><span class="ft-e">Example</span><span class="ft-n">Verified sales</span><span class="ft-p">Achieved (last 24 months)</span></div>
    <div class="ft-row" *ngFor="let rec of tableRows; trackBy: trackByRec" [class.ft-top]="rec.classification.token === '1x1'">
      <span class="ft-f">{{rec.classification.display}}</span>
      <span class="ft-e">{{exampleFor(rec)}}</span>
      <span class="ft-n">{{rec.sampleSize | number}}<em *ngIf="rec.confidence === 'Indicative'">{{basisLabel(rec)}}</em></span>
      <span class="ft-p">{{money(rec)}}</span>
    </div>
  </div>
  <p class="fmt-src">All-in achieved prices (hammer plus premium plus VAT plus assignment fee) on a rolling 24 month window, read from the PlateX sold-plate record. "Verified sales" is the total number of that format ever recorded, 1989 to 2026, a measure of how scarce each shape is, not the sample behind the figure. 1×1 shows a single sale, not a median: 1 F, £926,000 all-in, RM Sotheby's London, November 2025, the only 1×1 to come to public auction in the window and the highest price a UK registration has ever made.</p>

  <div class="inv-notes-2">
    <div class="note-card"><span class="nk">Note I</span>
      <h4>Original before reversed</h4>
      <p>A dateless registration reads in one of two orders. <b>Letters-first</b> marks, such as A 1 or ABC 123, are the original arrangement, issued from 1903. <b>Numbers-first</b> marks, such as 1000 E or 25 O, arrived only from 1953, once an authority had used up its forward combinations. Letters-first marks are therefore the older and the rarer, now just <b>6%</b> of all recorded dateless sales. Compared like for like, on the same format and the same years, they command a premium. A two-letter, one-number letters-first mark carries a median of <b>£43,300</b> against <b>£29,000</b> for its numbers-first equivalent, close to <b>1.5 times</b>; two-by-two marks show a <b>1.7 times</b> gap. The pattern points to originality: the earliest marks are the scarcest, and the market pays for it. Sample sizes at this level are small, so read the figures as indicative rather than exact.</p>
    </div>
    <div class="note-card"><span class="nk">Note II</span>
      <h4>Fewer letters, not more</h4>
      <p>Given a choice between more letters or more numbers, the market pays for <b>fewer letters</b>. Hold the overall length fixed, and value falls with each letter added. Among four-character marks, one letter with three numbers, <b>1×3</b> (£13,900), outsells two-and-two, <b>2×2</b> (£7,300), which in turn outsells three letters and one number, <b>3×1</b> (£6,200). The same order holds at every length. The reason is that a single county letter is the rarest element a plate can carry, so against the instinct to prize initials, one letter tends to outvalue a pair. The shortest mark of all, a single letter beside a single number, sits at the top.</p>
    </div>
  </div>

  <p class="inv-note">PlateX presents evidence, not advice. Every figure here is drawn from verified achieved sales; where a sample is small the figure is labelled indicative. Registrations are collectable assets whose values can fall as well as rise, nothing on this page is a recommendation to buy, or a forecast of future return.</p>
</div></section>

<!-- V · SEARCH, BROWSE + LIVE REGISTER (§6.8/§6.9 — browse pills live; search + register at Gate 3) -->
<section class="section tint" id="register"><div class="wrap">
  <div class="sec-head"><span class="eyebrow"><span class="ix">V</span>Explore the market</span>
    <h2>Search the register.</h2>
    <p>Search for a specific registration by its characters, or browse the live inventory by format. Both open the register below.</p>
  </div>

  <!-- classification-pill browser, senior scale (§6.8). No counts: none are
       genuine live inventory yet (§8). -->
  <div class="browse" role="list" aria-label="Browse dateless formats">
    <div class="bgrp" *ngFor="let g of formatGroups; trackBy: trackByGroup">
      <p class="bh">{{g.name}}</p>
      <div class="brow">
        <pla-classification-pill
          *ngFor="let f of g.formats; trackBy: trackByFormat"
          role="listitem"
          [label]="f.display"
          [describe]="f.plainEnglish"
          variant="format"
          size="lg"
          as="button"
          [active]="isSelected(f)"
          (selected)="selectFormat(f)"></pla-classification-pill>
      </div>
    </div>
    <p class="browse-key">letters × numbers, whichever order the plate shows them in. Both arrangements of a format share one register.</p>
  </div>

  <!-- character search (§6.8): both arrangements searched together -->
  <div class="searchbox" role="search">
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" aria-hidden="true"><circle cx="11" cy="11" r="7"/><path d="M21 21l-4.3-4.3"/></svg>
    <input [(ngModel)]="searchTerm"
           (keydown.enter)="runSearch()"
           placeholder="e.g. JB · 25 O · 7 · your initials"
           aria-label="Search the dateless register by characters"
           autocomplete="off"/>
    <button class="go" type="button" (click)="runSearch()">Search</button>
  </div>
  <p class="hints">Both arrangements, number first and letter first, are searched.</p>
</div></section>

<!-- THE LIVE REGISTER (§6.9) — the reference to marketplace handover -->
<section class="market-zone" aria-label="The live register"><div class="wrap mz-head-wrap">
  <div class="handover">
    <span class="ho-kicker">Reference&nbsp;→&nbsp;Archive&nbsp;→&nbsp;Live market</span>
    <span class="ho-title">The live register</span>
    <span class="ho-sub">You’re now leaving the reference, everything below is live inventory.</span>
  </div>

  <!-- selected-format header (§4.6: token always beside its plain-English
       definition); availability is the live feed count, nothing else -->
  <div class="mzone-head" *ngIf="selectedFormat" aria-live="polite">
    <span class="code">{{selectedFormat.display}}</span>
    <div class="mt">{{selectedFormat.plainEnglish}}<small>dateless · both arrangements share this register</small></div>
    <div class="mzone-stats">
      <!-- availability is shown ONLY when there is genuine live inventory;
           a zero is never rendered as a count (approved ruling): the empty
           register state carries the message instead, and the format itself
           remains valid. -->
      <div class="ms" *ngIf="regLoaded && !regFailed && regView.length > 0">
        <div class="v"><span class="live" aria-hidden="true"></span>{{regView.length | number}}</div>
        <div class="k">available now</div>
      </div>
      <div class="ms" *ngIf="selectedMedianRecord">
        <div class="v">{{money(selectedMedianRecord)}}</div>
        <div class="k">{{basisShort(selectedMedianRecord)}} (last 24 months)</div>
      </div>
    </div>
  </div>
  <p class="mz-none" *ngIf="!selectedFormat">Select a format above, or search, to open the live register.</p>
</div>

<div class="toolbar" *ngIf="selectedFormat"><div class="wrap in" role="group" aria-label="Register sort and filters">
  <span class="tsel"><span class="lab">Sort</span>
    <select [ngModel]="sort" (ngModelChange)="setSort($event)" aria-label="Sort the register">
      <option value="price-asc">Price · low to high</option>
      <option value="price-desc">Price · high to low</option>
      <option value="reg-az">Registration · A to Z</option>
    </select>
  </span>
  <span class="tchip" role="button" tabindex="0" [class.on]="arrangement === 'both'" (click)="setArrangement('both')" (keydown.enter)="setArrangement('both')">Both arrangements</span>
  <span class="tchip" role="button" tabindex="0" [class.on]="arrangement === 'number-first'" (click)="setArrangement('number-first')" (keydown.enter)="setArrangement('number-first')">Number first</span>
  <span class="tchip" role="button" tabindex="0" [class.on]="arrangement === 'letter-first'" (click)="setArrangement('letter-first')" (keydown.enter)="setArrangement('letter-first')">Letter first</span>
  <span class="tsel"><span class="lab">Dealer</span>
    <select [ngModel]="dealer || 'all'" (ngModelChange)="setDealer($event)" aria-label="Filter by dealer">
      <option value="all">All sellers</option>
      <option *ngFor="let d of dealers" [value]="d">{{d}}</option>
    </select>
  </span>
  <span class="tcount" role="status" aria-live="polite" *ngIf="regLoaded && !regFailed">
    Showing <b>{{regView.length ? ((page - 1) * pageSize + 1) : 0}} to {{page * pageSize < regView.length ? page * pageSize : regView.length}}</b> of <b>{{regView.length | number}}</b>
  </span>
</div></div>

<div class="wrap mz-body" *ngIf="selectedFormat">
  <!-- loading skeleton -->
  <div class="reg-skeleton" *ngIf="regLoading" aria-hidden="true">
    <div class="skel-row" *ngFor="let s of [1,2,3,4,5,6]"></div>
  </div>

  <!-- error -->
  <div class="reg-error" *ngIf="regFailed && !regLoading" role="alert">
    <p>The live register could not be loaded.</p>
    <button class="amg-c dark" type="button" (click)="selectFormat(selectedFormat)">Try again</button>
  </div>

  <!-- empty -->
  <p class="reg-empty" *ngIf="regLoaded && !regFailed && !regLoading && regView.length === 0">
    <ng-container *ngIf="regRows.length === 0">
      {{selectedFormat?.display}} is a valid dateless format, but no verified listings are on the market right now. Try another format, or search for a specific registration.
    </ng-container>
    <ng-container *ngIf="regRows.length > 0">
      No verified listings match these filters. Widen the arrangement or dealer filter.
    </ng-container>
  </p>

  <!-- results: the register-page row pattern (as Number Ones), with
       regulation-proportion YELLOW plates — live inventory, not archive
       (black-and-silver is reserved for history; build pack §10) -->
  <div class="reg-rows" *ngIf="!regLoading && !regFailed && regPage.length">
    <div class="reg-row" *ngFor="let row of regPage; trackBy: trackByRow">
      <a class="plate-link" [routerLink]="plateLink(row.registration)" [attr.aria-label]="'View ' + plateText(row)">
        <span class="plate-yl"><span class="ch">{{plateText(row)}}</span></span>
      </a>
      <span class="reg-acts">
        <pla-inline-favourite [plate]="row" [showText]="false"></pla-inline-favourite>
      </span>
      <span class="reg-arr">{{arrangementLabel(row)}}</span>
      <span class="reg-price"><em *ngIf="!isPOA(row)">FROM</em>{{price(row)}}</span>
      <a class="reg-view" [routerLink]="plateLink(row.registration)">View</a>
    </div>
  </div>

  <!-- pagination (client-side; the feed returns the complete set, measured) -->
  <nav class="pager" *ngIf="pageCount > 1" aria-label="Register pages">
    <button class="pg" type="button" [disabled]="page === 1" (click)="setPage(page - 1)" aria-label="Previous page">‹</button>
    <button class="pg" type="button"
            *ngFor="let p of pageNumbers"
            [class.on]="p === page"
            [attr.aria-current]="p === page ? 'page' : null"
            (click)="setPage(p)">{{p}}</button>
    <button class="pg" type="button" [disabled]="page === pageCount" (click)="setPage(page + 1)" aria-label="Next page">›</button>
  </nav>
</div></section>

<!-- VI · RELATED COLLECTIONS (§6.10) -->
<section class="section" id="collections"><div class="wrap">
  <div class="sec-head"><span class="eyebrow"><span class="ix">VI</span>Related collections</span>
    <h2>The same plates, other stories.</h2>
    <p>A dateless registration can belong to more than one collection at once. Follow the thread that fits what you collect.</p>
  </div>
  <div class="coll-grid">
    <ng-container *ngFor="let c of collections">
      <a class="coll" *ngIf="c.link" [routerLink]="c.link">
        <div class="ct">{{c.title}}</div>
        <div class="cd">{{c.description}}</div>
        <div class="ca">{{c.action}}</div>
      </a>
      <a class="coll" *ngIf="!c.link && c.anchor" href="#{{c.anchor}}" (click)="jumpTo(c.anchor!, $event)">
        <div class="ct">{{c.title}}</div>
        <div class="cd">{{c.description}}</div>
        <div class="ca">{{c.action}}</div>
      </a>
      <div class="coll is-static" *ngIf="!c.link && !c.anchor">
        <div class="ct">{{c.title}}</div>
        <div class="cd">{{c.description}}</div>
        <div class="ca" *ngIf="c.action">{{c.action}}</div>
      </div>
    </ng-container>
  </div>
</div></section>

<!-- In closing -->
<section class="section dark"><div class="wrap">
  <div class="sec-head"><span class="eyebrow">In closing</span>
    <h2>Every registration belongs to more than one story.</h2>
    <p>Dateless is one way of reading the PlateX collection. The same registration also sits within its format, its era and its market, and each of those is a separate route through the same catalogue.</p>
  </div>
  <div class="wn-grid">
    <ng-container *ngFor="let w of whereNext">
      <a class="wn" *ngIf="w.link" [routerLink]="w.link">
        <div class="wt">{{w.title}}</div>
        <div class="wa">{{w.action}}</div>
      </a>
      <div class="wn is-static" *ngIf="!w.link">
        <div class="wt">{{w.title}}</div>
      </div>
    </ng-container>
  </div>
</div></section>
